import { useEffect, useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import { cn } from "../lib/utils";

const writeClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
};

// Subtle clipboard icon: faint until the parent `.group` row/cell is hovered.
// Shows a "Copied" tooltip for 2s after clicking.
export const CopyButton = ({ text, label = "value", className, testId }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!text) return;
    await writeClipboard(String(text));
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copied" : `Copy ${label}`}
        aria-label={`Copy ${label}`}
        className={cn(
          "inline-flex items-center justify-center p-0.5 rounded transition-all",
          copied
            ? "opacity-100 text-emerald-400"
            : "opacity-25 group-hover:opacity-100 focus:opacity-100 text-slate-500 hover:text-blue-400",
          className
        )}
        data-testid={testId || `copy-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
      {copied && (
        <span
          className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded bg-emerald-500/90 text-[10px] font-semibold text-white whitespace-nowrap shadow pointer-events-none z-20"
          data-testid="copied-tooltip"
        >
          Copied
        </span>
      )}
    </span>
  );
};

// Inline value + copy icon. Wrap rows/cells with `group` so the icon reveals on hover.
export const CopyField = ({ value, label, children, className, mono = false, testId }) => {
  if (!value) return <span className={className}>{children ?? "—"}</span>;
  return (
    <span className={cn("group inline-flex items-center gap-1 max-w-full", className)}>
      <span className={cn("truncate", mono && "font-mono")}>{children ?? value}</span>
      <CopyButton text={value} label={label} testId={testId} />
    </span>
  );
};
